import client from '../client/client';
import { fetchStaticJson } from '../../config/fetchStaticJson';

/**
 * Maps admin route botName to discovery-icons fallback filename (bucket folder name).
 */
const ROUTE_BOT_TO_BUCKET_KEY = {
  regulatory: 'frida',
  frida_config: 'frida',
  agadati_config: 'unison',
  myedu_support_config: 'myedu',
  sifrut_regulatory_config: 'safrot',
  gemer_regulatory_config: 'gemer',
  hemed_regulatory_config: 'hemed',
  mili_config: 'mili',
  mili: 'mili',
  realy_config: 'realy',
  modi: 'realy',
};

function fallbackUrl(bucketKey) {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}/config/discovery-icons/${bucketKey}.json`;
}

function apiUrl(bucketKey) {
  const baseApiUrl = window.config?.baseApiUrl;
  if (!baseApiUrl) return null;
  return `${baseApiUrl.replace(/\/$/, '')}/adminapi/bots/${encodeURIComponent(bucketKey)}/discovery-icons`;
}

export function resolveDiscoveryIconsBucketKey(botName, draft) {
  const fromIconUrl = extractBucketFolderFromUrl(draft?.botIconUrl);
  if (fromIconUrl) {
    return fromIconUrl;
  }

  if (ROUTE_BOT_TO_BUCKET_KEY[botName]) {
    return ROUTE_BOT_TO_BUCKET_KEY[botName];
  }

  const normalized = botName.replace(/_config$/, '').replace(/_regulatory_config$/, '');
  return ROUTE_BOT_TO_BUCKET_KEY[normalized] || normalized;
}

function extractBucketFolderFromUrl(url) {
  if (typeof url !== 'string') return null;
  const match = url.match(/\/bucketfiles\/bots\/([^/]+)\//i);
  return match ? match[1] : null;
}

function normalizeIconList(data) {
  if (!Array.isArray(data)) {
    return [];
  }

  return data
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const url = item.url || item.icon;
      if (!url) return null;
      const id =
        item.id ||
        String(url)
          .split('/')
          .pop()
          ?.replace(/\.svg$/i, '') ||
        url;
      return {
        id,
        url,
        label: item.label || id,
      };
    })
    .filter(Boolean);
}

async function fetchFromApi(bucketKey) {
  const url = apiUrl(bucketKey);
  if (!url) {
    return null;
  }

  try {
    const res = await client.get(url);
    if (!res?.ok) {
      return null;
    }
    const data = await res.json();
    const icons = normalizeIconList(data);
    return icons.length > 0 ? icons : null;
  } catch {
    return null;
  }
}

async function fetchFromFallback(bucketKey) {
  try {
    const data = await fetchStaticJson(fallbackUrl(bucketKey));
    return normalizeIconList(data);
  } catch {
    return [];
  }
}

export async function fetchDiscoveryIcons(botName, draft) {
  const bucketKey = resolveDiscoveryIconsBucketKey(botName, draft);
  const fromApi = await fetchFromApi(bucketKey);
  if (fromApi) {
    return { icons: fromApi, bucketKey, source: 'api' };
  }

  const fromFallback = await fetchFromFallback(bucketKey);
  return { icons: fromFallback, bucketKey, source: 'fallback' };
}

export function uniqueIconsFromButtons(buttons) {
  if (!Array.isArray(buttons)) {
    return [];
  }

  const seen = new Set();
  const out = [];

  buttons.forEach((btn) => {
    const url = btn?.icon;
    if (!url || seen.has(url)) return;
    seen.add(url);
    const id = String(url)
      .split('/')
      .pop()
      ?.replace(/\.svg$/i, '') || url;
    out.push({ id, url, label: id });
  });

  return out;
}
